import { useAuth0 } from '@auth0/auth0-react'
import { useOrgUser } from '../context/OrgUserProvider'
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  FormHelperText
} from '@mui/material';
import { Add, Close, CloudUpload, Delete } from '@mui/icons-material';
import { saveS3, getS3Object } from '../utils/functions'

const CreateCourseDialog = ({ open, onClose, onCourseCreated }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [slugUniquenessError, setSlugUniquenessError] = useState('');
  const [nextCourseId, setNextCourseId] = useState(1);
  const fileInputRef = useRef(null);

  // Settings
  const { getAccessTokenSilently } = useAuth0()
  const { orgUser } = useOrgUser()

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    level: 'Basic',
    duration: '',
    lessons_count: '',
    slug: '',
    image_url: '',
    category: ''
  });

  const levels = ['Basic', 'Intermediate', 'Advanced'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
  const RECOMMENDED_DIMENSIONS = { width: 1920, height: 1080 };

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      resetForm();
      fetchCategories();
      getNextCourseId();
    }
  }, [open]);

  const getNextCourseId = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: 'https://qallix-api',
      });
      const orgName = orgUser.orgName;

      // Try to get the existing manifest
      let manifest = { courses: [] };
      try {
        const manifestResponse = await getS3Object(
          `QallixAcademy/SoftwareCourses/course-manifest.json`,
          'qallix-public',
          token,
          orgName
        );

        if (manifestResponse.statusCode === 200) {
          const manifestData = JSON.parse(atob(manifestResponse.fileContent));
          manifest = manifestData;
        }
      } catch (error) {
        // Manifest doesn't exist yet, that's fine
        console.error('No existing manifest found, starting with ID 1');
      }

      // Calculate the next ID
      const maxId = manifest.courses.length > 0 
        ? Math.max(...manifest.courses.map(c => c.id)) 
        : 0;
      
      setNextCourseId(maxId + 1);
    } catch (error) {
      console.error('Error getting next course ID:', error);
      setNextCourseId(1); // Fallback to 1
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const token = await getAccessTokenSilently({
        audience: 'https://qallix-api',
      });
      const orgName = orgUser.orgName;

      // Get the categories
      const categoryResponse = await getS3Object(
        'QallixAcademy/SoftwareCourses/category.json',
        'qallix-public',
        token,
        orgName
      );

      if (categoryResponse.statusCode !== 200) {
        throw new Error('Failed to fetch categories');
      }

      const categoryData = JSON.parse(atob(categoryResponse.fileContent));
      const allCategories = categoryData.categories || [];

      // Filter only active categories
      const activeCategories = allCategories
        .filter(category => category.isActive)
        .map(category => category.name);

      setCategories(activeCategories);

      // Set the first category as default if available
      if (activeCategories.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: activeCategories[0] }));
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoadingCategories(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Title validation
    if (!formData.title.trim()) {
      errors.title = 'Course title is required';
    } else if (formData.title.length < 5) {
      errors.title = 'Title must be at least 5 characters';
    } else if (formData.title.length > 100) {
      errors.title = 'Title must be less than 100 characters';
    }

    // Slug validation
    if (!formData.slug.trim()) {
      errors.slug = 'Slug is required';
    } else if (formData.slug.includes(' ')) {
      errors.slug = 'Slug cannot contain spaces';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      errors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    } else if (formData.slug.length < 3) {
      errors.slug = 'Slug must be at least 3 characters';
    } else if (formData.slug.length > 50) {
      errors.slug = 'Slug must be less than 50 characters';
    } else if (formData.slug.startsWith('-') || formData.slug.endsWith('-')) {
      errors.slug = 'Slug cannot start or end with a hyphen';
    } else if (formData.slug.includes('--')) {
      errors.slug = 'Slug cannot contain consecutive hyphens';
    }

    // Description validation
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    } else if (formData.description.length < 20) {
      errors.description = 'Description must be at least 20 characters';
    } else if (formData.description.length > 1000) {
      errors.description = 'Description must be less than 1000 characters';
    }

    // Duration validation
    if (!formData.duration) {
      errors.duration = 'Duration is required';
    } else if (parseFloat(formData.duration) <= 0) {
      errors.duration = 'Duration must be greater than 0';
    } else if (parseFloat(formData.duration) > 100) {
      errors.duration = 'Duration must be less than 100 hours';
    }

    // Lessons count validation
    if (!formData.lessons_count) {
      errors.lessons_count = 'Number of lessons is required';
    } else if (parseInt(formData.lessons_count) <= 0) {
      errors.lessons_count = 'Must have at least 1 lesson';
    } else if (parseInt(formData.lessons_count) > 100) {
      errors.lessons_count = 'Cannot have more than 100 lessons';
    }

    // Category validation
    if (!formData.category) {
      errors.category = 'Category is required';
    }

    // Image validation
    if (!imageFile && !formData.image_url) {
      errors.image = 'Course thumbnail is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = () => {
    return Object.keys(validationErrors).length === 0 && !slugUniquenessError;
  };

  const shouldShowError = (fieldName) => {
    return (touchedFields[fieldName] || submitAttempted) && validationErrors[fieldName];
  };

  const checkSlugUniqueness = async (proposedSlug) => {
    try {
      const token = await getAccessTokenSilently({
        audience: 'https://qallix-api',
      });
      const orgName = orgUser.orgName;

      // Try to get the existing manifest
      let manifest = { courses: [] };
      try {
        const manifestResponse = await getS3Object(
          `QallixAcademy/SoftwareCourses/course-manifest.json`,
          'qallix-public',
          token,
          orgName
        );

        if (manifestResponse.statusCode === 200) {
          const manifestData = JSON.parse(atob(manifestResponse.fileContent));
          manifest = manifestData;
        }
      } catch (error) {
        // Manifest doesn't exist yet, that's fine
        console.error('No existing manifest found, all slugs are unique.');
      }

      // Check if the proposed slug is already in the manifest
      const slugExists = manifest.courses.some(course => 
        course.slug === proposedSlug && !course.isDeleted
      );
      
      if (slugExists) {
        setSlugUniquenessError('This slug is already in use. Please choose a different one.');
        return false;
      } else {
        setSlugUniquenessError('');
        return true;
      }

    } catch (error) {
      console.error('Error checking slug uniqueness:', error);
      setSlugUniquenessError('Error checking slug availability. Please try again.');
      return false;
    }
  };

  const handleInputChange = async (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [field]: true }));

    // Auto-generate slug from title
    if (field === 'title') {
      const generatedSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '')
        .replace(/--+/g, '-');

      setFormData(prev => ({
        ...prev,
        slug: generatedSlug
      }));
      setTouchedFields(prev => ({ ...prev, slug: true }));
      
      // Check uniqueness of the generated slug
      if (generatedSlug) {
        await checkSlugUniqueness(generatedSlug);
      }
    }

    // Check slug uniqueness when slug field changes
    if (field === 'slug') {
      if (value) {
        await checkSlugUniqueness(value);
      } else {
        setSlugUniquenessError('');
      }
    }

    // Validate the field
    validateForm();
  };

  const handleBlur = async (field) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    
    // Check slug uniqueness when slug field loses focus
    if (field === 'slug' && formData.slug) {
      await checkSlugUniqueness(formData.slug);
    }
    
    validateForm();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Clear previous errors
    setError('');
    setValidationErrors(prev => ({ ...prev, image: '' }));

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setValidationErrors(prev => ({
        ...prev,
        image: 'Please select a valid image file (JPEG, PNG, or WebP)'
      }));
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setValidationErrors(prev => ({
        ...prev,
        image: 'File size must be less than 5MB'
      }));
      return;
    }

    setImageFile(file);
    setTouchedFields(prev => ({ ...prev, image: true }));

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);

      // Check image dimensions
      const img = new Image();
      img.onload = () => {
        if (img.width !== RECOMMENDED_DIMENSIONS.width || img.height !== RECOMMENDED_DIMENSIONS.height) {
          setValidationErrors(prev => ({
            ...prev,
            image: `Recommended dimensions: ${RECOMMENDED_DIMENSIONS.width}x${RECOMMENDED_DIMENSIONS.height}px. Your image is ${img.width}x${img.height}px.`
          }));
        } else {
          // Clear dimension error if dimensions are correct
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            if (newErrors.image && newErrors.image.includes('Recommended dimensions')) {
              delete newErrors.image;
            }
            return newErrors;
          });
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, image_url: '' }));
    setValidationErrors(prev => ({ ...prev, image: 'Course thumbnail is required' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateCourseManifest = async (courseData, token, orgName) => {
    try {
      // Try to get the existing manifest
      let manifest = { courses: [] };
      try {
        const manifestResponse = await getS3Object(
          `QallixAcademy/SoftwareCourses/course-manifest.json`,
          'qallix-public',
          token,
          orgName
        );

        if (manifestResponse.statusCode === 200) {
          const manifestData = JSON.parse(atob(manifestResponse.fileContent));
          manifest = manifestData;
        }
      } catch (error) {
      }

      // Add the new course to the manifest
      const newCourse = {
        id: courseData.id, // Use the generated ID
        slug: courseData.slug,
        isDeleted: false
      };
      
      manifest.courses.push(newCourse);

      // Upload the updated manifest
      const manifestBlob = new Blob([JSON.stringify(manifest, null, 2)], {
        type: 'application/json'
      });

      const manifestFile = new File([manifestBlob], 'course-manifest.json', {
        type: 'application/json'
      });

      const manifestFileForUpload = {
        body: manifestFile,
        key: `QallixAcademy/SoftwareCourses/course-manifest.json`,
        name: 'course-manifest.json'
      };

      await saveS3([manifestFileForUpload], token, orgName, 'qallix-public', true, false);
      return true;

    } catch (error) {
      console.error('Error updating course manifest:', error);
      throw new Error('Failed to update course manifest');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    // Check slug uniqueness one more time before submitting
    const isSlugUnique = await checkSlugUniqueness(formData.slug);
    if (!isSlugUnique) {
      setError('Please fix the validation errors before submitting.');
      return;
    }

    if (!validateForm()) {
      setError('Please fix the validation errors before submitting.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get auth token and organization name
      const token = await getAccessTokenSilently({
        audience: 'https://qallix-api',
      })

      let imageUrl = formData.image_url;

      // Upload image to S3 if a new image was selected
      if (imageFile) {
        setUploading(true);
        const imageFileForUpload = {
          body: imageFile,
          key: `QallixAcademy/SoftwareCourses/${nextCourseId}/thumbnail.${imageFile.name.split('.').pop()}`
        };

        const imageResults = await saveS3([imageFileForUpload], token, orgUser.orgName, 'qallix-public', true, false);
        imageUrl = imageResults[0];
        setUploading(false);
      }

      // Create course-info.json in the specified format
      const courseInfo = {
        id: nextCourseId, // Add the ID to course info
        title: formData.title,
        description: formData.description,
        noOflessons: parseInt(formData.lessons_count),
        level: formData.level,
        category: formData.category,
        slug: formData.slug,
        duration: parseFloat(formData.duration),
        status: 'Draft',
        thumbnail: `thumbnail.${imageFile ? imageFile.name.split('.').pop() : 'png'}` // Relative path
      };

      // Convert courseInfo to a File object for S3 upload
      const jsonBlob = new Blob([JSON.stringify(courseInfo, null, 2)], {
        type: 'application/json'
      });

      const jsonFile = new File([jsonBlob], 'course-info.json', {
        type: 'application/json'
      });

      // Upload JSON file to S3
      const jsonFileForUpload = {
        body: jsonFile,
        key: `QallixAcademy/SoftwareCourses/${nextCourseId}/course-info.json`
      };

      const jsonResults = await saveS3([jsonFileForUpload], token, orgUser.orgName, 'qallix-public', true, false);
      
      // Update the course manifest with the new course
      await updateCourseManifest(courseInfo, token, orgUser.orgName);
      
      const jsonFileUrl = jsonResults[0];

      // Call the success callback with the course data
      if (onCourseCreated) {
        onCourseCreated();
      }

      onClose();
      resetForm();

    } catch (err) {
      setError(err.message);
      console.error('Error creating course:', err);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      level: 'Basic',
      duration: '',
      lessons_count: '',
      slug: '',
      image_url: '',
      category: categories.length > 0 ? categories[0] : ''
    });
    setImageFile(null);
    setImagePreview('');
    setError('');
    setValidationErrors({});
    setTouchedFields({});
    setSubmitAttempted(false);
    setSlugUniquenessError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontFamily="'Satoshi', sans-serif">
            Create New Course (ID: {nextCourseId})
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
            <TextField
              fullWidth
              label="Course Title *"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              onBlur={() => handleBlur('title')}
              error={shouldShowError('title')}
              helperText={shouldShowError('title') ? validationErrors.title : ''}
              InputProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
              InputLabelProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
              FormHelperTextProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
            />
            <TextField
              fullWidth
              label="Slug *"
              value={formData.slug}
              onChange={(e) => handleInputChange('slug', e.target.value)}
              onBlur={() => handleBlur('slug')}
              error={shouldShowError('slug') || !!slugUniquenessError}
              helperText={
                shouldShowError('slug') 
                  ? validationErrors.slug 
                  : slugUniquenessError 
                    ? slugUniquenessError 
                    : "URL-friendly identifier (no spaces)"
              }
              InputProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
              InputLabelProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
              FormHelperTextProps={{ 
                sx: { 
                  fontFamily: "'Satoshi', sans-serif",
                  color: slugUniquenessError ? '#d32f2f' : 'inherit'
                } 
              }}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description *"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            onBlur={() => handleBlur('description')}
            error={shouldShowError('description')}
            helperText={shouldShowError('description') ? validationErrors.description : ''}
            sx={{ mb: 2 }}
            InputProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
            InputLabelProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
            FormHelperTextProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
          />

          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
            <FormControl fullWidth error={shouldShowError('level')}>
              <InputLabel sx={{ fontFamily: "'Satoshi', sans-serif" }}>Level *</InputLabel>
              <Select
                value={formData.level}
                label="Level *"
                onChange={(e) => handleInputChange('level', e.target.value)}
                onBlur={() => handleBlur('level')}
                sx={{ fontFamily: "'Satoshi', sans-serif" }}
              >
                {levels.map(level => (
                  <MenuItem key={level} value={level} sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
              {shouldShowError('level') && (
                <FormHelperText sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                  {validationErrors.level}
                </FormHelperText>
              )}
            </FormControl>

            <TextField
              fullWidth
              type="number"
              step="0.5"
              label="Duration (hours) *"
              value={formData.duration}
              onChange={(e) => handleInputChange('duration', e.target.value)}
              onBlur={() => handleBlur('duration')}
              error={shouldShowError('duration')}
              helperText={shouldShowError('duration') ? validationErrors.duration : "0.5 = 30 minutes, 1 = 1 hour"}
              InputProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
              InputLabelProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
              FormHelperTextProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
            />
          </Box>

          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
            <FormControl fullWidth error={shouldShowError('category')}>
              <InputLabel sx={{ fontFamily: "'Satoshi', sans-serif" }}>Category *</InputLabel>
              <Select
                value={formData.category}
                label="Category *"
                onChange={(e) => handleInputChange('category', e.target.value)}
                onBlur={() => handleBlur('category')}
                sx={{ fontFamily: "'Satoshi', sans-serif" }}
                disabled={loadingCategories || categories.length === 0}
              >
                {loadingCategories ? (
                  <MenuItem value="" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                    Loading categories...
                  </MenuItem>
                ) : categories.length === 0 ? (
                  <MenuItem value="" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                    No categories available
                  </MenuItem>
                ) : (
                  categories.map(category => (
                    <MenuItem key={category} value={category} sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                      {category}
                    </MenuItem>
                  ))
                )}
              </Select>
              {shouldShowError('category') && (
                <FormHelperText sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                  {validationErrors.category}
                </FormHelperText>
              )}
            </FormControl>
            <TextField
              fullWidth
              type="number"
              label="Number of Lessons *"
              value={formData.lessons_count}
              onChange={(e) => handleInputChange('lessons_count', e.target.value)}
              onBlur={() => handleBlur('lessons_count')}
              error={shouldShowError('lessons_count')}
              helperText={shouldShowError('lessons_count') ? validationErrors.lessons_count : ''}
              InputProps={{
                sx: { fontFamily: "'Satoshi', sans-serif" },
                inputProps: { min: 1, max: 100 }
              }}
              InputLabelProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
              FormHelperTextProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
            />
          </Box>

          <Box mb={2}>
            <Typography variant="body2" sx={{ mb: 1, fontFamily: "'Satoshi', sans-serif" }}>
              Course Thumbnail *
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              disabled={uploading}
              sx={{ fontFamily: "'Satoshi', sans-serif" }}
            >
              Upload Image
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp,image/jpg"
                onChange={handleFileSelect}
              />
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, fontFamily: "'Satoshi', sans-serif" }}>
              Max 5MB • Recommended: {RECOMMENDED_DIMENSIONS.width}x{RECOMMENDED_DIMENSIONS.height}px
            </Typography>
            {shouldShowError('image') && (
              <FormHelperText error sx={{ fontFamily: "'Satoshi', sans-serif", mt: 1 }}>
                {validationErrors.image}
              </FormHelperText>
            )}
          </Box>

          {(imagePreview || formData.image_url) && (
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" fontFamily="'Satoshi', sans-serif">
                  Image Preview:
                </Typography>
                <Button
                  size="small"
                  color="error"
                  startIcon={<Delete />}
                  onClick={removeImage}
                  sx={{ fontFamily: "'Satoshi', sans-serif" }}
                >
                  Remove
                </Button>
              </Box>
              <Box
                component="img"
                src={imagePreview || formData.image_url}
                alt="Course preview"
                sx={{
                  width: '100%',
                  height: '200px',
                  objectFit: 'contain',
                  borderRadius: 1,
                  border: '1px solid #ddd',
                  backgroundColor: '#f5f5f5'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading || uploading}
            variant="outlined"
            sx={{
              color: '#332872',
              borderColor: '#332872',
              fontFamily: "'Satoshi', sans-serif",
              '&:hover': { borderColor: '#2c2458ff', backgroundColor: 'rgba(51, 40, 114, 0.04)' }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || uploading || (submitAttempted && !isFormValid()) || categories.length === 0}
            sx={{
              backgroundColor: '#332872',
              fontFamily: "'Satoshi', sans-serif",
              '&:hover': { backgroundColor: '#2c2458ff' },
              '&:disabled': { backgroundColor: '#cccccc', color: '#666666' }
            }}
            startIcon={(loading || uploading) ? <CircularProgress size={16} /> : <Add />}
          >
            {(loading || uploading) ? 'Processing...' : 'Add Course'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateCourseDialog;